import { useAuth0 } from '@auth0/auth0-react';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { Currencies, PaymentRails, Networks, Tokens } from './useTransfer';
import { useEffect, useRef } from 'react';
import { useDebouncedValue } from '../libs/useDebouncedValue';
import { Form, FormInstance } from 'antd';
import { useBackoffice } from '../backoffice/useBackoffice';

type QuotePayload = {
  payload: {
    customer_id: string;
    transfer_type: string;
    offramp?: {
      source_amount?: string;
      destination_amount?: string;
      source_token: Tokens;
      source_network: Networks;
      destination_currency: Currencies;
      destination_payment_rails: PaymentRails;
    };
  };
};

export const useOfframpQuote = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { developerId } = useBackoffice();
  const offrampQuoteQuery = useMutation({
    mutationKey: ['offramp-quote'],
    mutationFn: async (payload: QuotePayload) => {
      const token = await getAccessTokenSilently();
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/customers/transfers/quote`,
        payload.payload,
        {
          params: {
            consumerId: developerId,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return res.data?.data;
    },
  });

  return offrampQuoteQuery;
};

export const useOfframpQuoteFormAware = (
  form: FormInstance,
  editingField: 'offramp.source_amount' | 'offramp.destination_amount' | null
) => {
  const sourceAmount = Form.useWatch('offramp.source_amount', form);
  const destinationAmount = Form.useWatch('offramp.destination_amount', form);
  const sourceNetwork = Form.useWatch('offramp.source_network', form);
  const sourceToken = Form.useWatch('offramp.source_token', form);
  const destinationRails = Form.useWatch('offramp.destination_payment_rails', form);
  const destinationCurrency = Form.useWatch('offramp.destination_currency', form);
  const customerId = Form.useWatch('customer_id', form);
  const transferType = Form.useWatch('type', form);

  const debouncedSource = useDebouncedValue(editingField === 'offramp.source_amount' ? sourceAmount : null, 1000);
  const debouncedDestination = useDebouncedValue(
    editingField === 'offramp.destination_amount' ? destinationAmount : null,
    1000
  );

  const { mutate: fetchQuote, data: quoteData, isPending: isQuoteLoading, reset } = useOfframpQuote();
  // Track which editingField the quoteData was fetched for
  const quoteForFieldRef = useRef<string | null>(null);

  useEffect(() => {
    reset();
  }, [editingField, reset]);

  useEffect(() => {
    const debouncedAmount = debouncedSource || debouncedDestination;
    const focusedField = debouncedSource ? 'offramp.source_amount' : 'offramp.destination_amount';

    if (
      transferType !== 'offramp' ||
      !customerId ||
      !debouncedAmount ||
      !sourceNetwork ||
      !sourceToken ||
      !destinationRails ||
      !destinationCurrency
    ) {
      return;
    }

    const cleanAmount = String(debouncedAmount).replace(/,/g, '');
    if (cleanAmount.endsWith('.')) {
      return;
    }
    const numAmount = parseFloat(cleanAmount);

    if (isNaN(numAmount) || !isFinite(numAmount) || numAmount <= 0) {
      return;
    }

    quoteForFieldRef.current = focusedField;

    fetchQuote({
      payload: {
        customer_id: customerId,
        transfer_type: transferType,
        offramp: {
          source_amount: focusedField === 'offramp.source_amount' ? cleanAmount : undefined,
          destination_amount: focusedField === 'offramp.destination_amount' ? cleanAmount : undefined,
          source_token: sourceToken,
          source_network: sourceNetwork,
          destination_currency: destinationCurrency,
          destination_payment_rails: destinationRails,
        },
      },
    });
  }, [
    debouncedSource,
    debouncedDestination,
    customerId,
    sourceNetwork,
    sourceToken,
    destinationRails,
    destinationCurrency,
    transferType,
    fetchQuote,
  ]);

  useEffect(() => {
    if (!quoteData) return;

    // Only update if quoteData matches the current editingField
    if (quoteForFieldRef.current !== editingField) {
      return;
    }

    if (editingField === 'offramp.source_amount' && quoteData.offramp.destination_amount) {
      form.setFieldValue('offramp.destination_amount', quoteData.offramp.destination_amount);
    } else if (editingField === 'offramp.destination_amount' && quoteData.offramp.source_amount) {
      console.log('lol');
      form.setFieldValue('offramp.source_amount', quoteData.offramp.source_amount);
    }
  }, [quoteData, editingField, form]);

  return {
    isQuoteLoading,
    quoteData,
  };
};
